import React, { useEffect, useRef, useState } from 'react';
import { FaBars, FaTimes } from 'react-icons/fa';
import logo from '../assets/logo.png';
import eVerifyLogo from '../assets/e-verify-logo.png';

const navItems = [
  ['home', 'Home'],
  ['about', 'About Us'],
  ['services', 'Solutions'],
  ['industries', 'Industries'],
  ['partners', 'Compliance'],
  ['contact', 'Contact Us']
];

function Header() {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const lockedSectionRef = useRef(null);
  const lockTimeoutRef = useRef(null);
  const revealTimeoutRef = useRef(null);

  useEffect(() => {
    let animationFrameId = null;

    const updateActiveSection = () => {
      if (lockedSectionRef.current) {
        return;
      }

      const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
      const scrollPosition = window.scrollY + headerHeight + 24;
      const sections = navItems
        .map(([sectionId]) => document.getElementById(sectionId))
        .filter(Boolean);
      let currentSection = sections[0];

      sections.forEach(section => {
        if (section.offsetTop <= scrollPosition) {
          currentSection = section;
        }
      });

      setActiveSection(currentSection?.id || 'home');
    };

    const handleScroll = () => {
      if (animationFrameId) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        updateActiveSection();
        animationFrameId = null;
      });
    };

    updateActiveSection();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateActiveSection);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (lockTimeoutRef.current) {
        window.clearTimeout(lockTimeoutRef.current);
      }
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
      }
    };
  }, []);

  const scrollToSection = (event, sectionId) => {
    event.preventDefault();

    const target = document.getElementById(sectionId);
    if (!target) {
      return;
    }

    lockedSectionRef.current = sectionId;
    setActiveSection(sectionId);
    setIsMenuOpen(false);
    window.history.pushState(null, '', `#${sectionId}`);

    const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
    const top = Math.max(target.getBoundingClientRect().top + window.scrollY - headerHeight - 8, 0);

    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;

    target.classList.remove('section-reveal');
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, top);

    window.requestAnimationFrame(() => {
      root.style.scrollBehavior = previousScrollBehavior;
      target.classList.add('section-reveal');
    });

    if (revealTimeoutRef.current) {
      window.clearTimeout(revealTimeoutRef.current);
    }

    revealTimeoutRef.current = window.setTimeout(() => {
      target.classList.remove('section-reveal');
    }, 760);

    if (lockTimeoutRef.current) {
      window.clearTimeout(lockTimeoutRef.current);
    }

    lockTimeoutRef.current = window.setTimeout(() => {
      lockedSectionRef.current = null;
    }, 850);
  };

  return (
    <header className="site-header">
      <div className="site-header-inner">
        <div className="site-branding">
          <a
            className="brand-mark"
            href="#home"
            onClick={(event) => scrollToSection(event, 'home')}
          >
            <img src={logo} alt="Dhriti Solutions Logo" className="brand-logo" />
            <span className="brand-name">
              Dhriti <span>Solutions</span>
            </span>
          </a>
          <img
            src={eVerifyLogo}
            alt="E-Verify"
            className="header-everify-logo"
          />
        </div>

        <button
          className="mobile-menu-button"
          type="button"
          aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isMenuOpen}
          aria-controls="primary-navigation"
          onClick={() => setIsMenuOpen(open => !open)}
        >
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <nav id="primary-navigation" className={`primary-nav ${isMenuOpen ? 'is-open' : ''}`}>
          <ul className="nav-list">
            {navItems.map(([sectionId, label]) => (
              <li key={sectionId}>
                <a
                  href={`#${sectionId}`}
                  className={`nav-link ${sectionId === activeSection ? 'is-active' : ''}`}
                  aria-current={sectionId === activeSection ? 'page' : undefined}
                  onClick={(event) => scrollToSection(event, sectionId)}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
}

export default Header;
